'use client';

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { ToastContext } from '../../Toast';
import {
  useCartState,
  useCartUI,
  useDiscountManager,
  useCartOperations,
} from '../hooks';
import { deriveCartType, findUhxLine, deriveIsAllDigital } from '../derive';
import { CartStoreContext } from '../store/CartStoreContext';
import { createCartStore } from '../store/cartStore';
import {
  CART_ANALYTICS_EVENTS,
  CART_GTM_EVENTS,
  pushEcommerceEvent,
  trackFunnelEvent,
  type EcommerceItem,
} from 'components/shared/UserAnalytics/funnelEvents';

/** GA4 `items[]` from the cart's lines; attribute `_product_type` becomes the category. */
const cartEcommerceItems = (cart: Record<string, any>): EcommerceItem[] =>
  (cart?.lines?.edges ?? []).map((edge: any) => {
    const node = edge.node ?? {};
    const attrs: Array<{ key: string; value: string }> = node.attributes ?? [];
    return {
      item_id: String(node.merchandise?.id ?? '').replace(
        'gid://shopify/ProductVariant/',
        '',
      ),
      item_name: String(
        node.merchandise?.product?.title ?? node.merchandise?.title ?? '',
      ),
      item_variant: node.merchandise?.title
        ? String(node.merchandise.title)
        : undefined,
      item_category: attrs.find((a) => a.key === '_product_type')?.value,
      price: Number(node.cost?.amountPerQuantity?.amount ?? 0),
      quantity: Number(node.quantity ?? 1),
    };
  });
import {
  addToCartArgsType,
  CartFlow,
  CartFlowScreens,
  ProductType,
} from '../types';
import { useFormatLink } from '../../CustomLink';
import {
  clearUltraworkSession,
  getWorkAffiliate,
  getWorkAffiliateDiscounts,
  readUltraworkSession,
  useAffiliateMetadata,
} from 'utils/affiliateHelpers';
import { workAffiliateDiscountType } from '../types';
import { ProductPurchaseType } from 'content/products';
import {
  referralCodes,
  ringReferralCodes,
  ringProReferralCodes,
} from 'content/referral';
import { deleteFromLocalStorage, makeStoreApiRequest } from '../utils';
import { applyRingAirGiftStamp } from '../giftStamp';
import { GiftWrappingProductShopifyMap } from 'content/products/giftWrappingProduct';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { getCookie } from 'utils/cookie';
import { RouteIntlScope } from 'lib/i18n/routeIntl';
import {
  getCampaignByProduct,
  Campaign,
  SddDeliveryBanner,
} from 'content/campaigns';
import { useCampaigns } from 'hooks/campaigns/useCampaigns';

const UpsellModal = dynamic(() => import('../UpsellModal'), { ssr: false });

export const CartProvider = ({
  children,
  initialCampaigns,
  initialSddDelivery,
}: {
  children: React.ReactNode;
  initialCampaigns?: Campaign[];
  initialSddDelivery?: SddDeliveryBanner | null;
}) => {
  // Discount codes ref at provider level to maintain persistence across renders
  const discountCodes = useRef<Array<string> | []>([]);
  const router = useRouter();
  const searchParams = useSearchParams();
  const internalPathname = useInternalPathname();
  const t = useTranslations('cart');
  const { showToast } = useContext(ToastContext);
  const routePath =
    internalPathname.length > 1
      ? internalPathname.replace(/\/$/, '')
      : internalPathname;
  const isShopPage = routePath === '/shop';

  // Initialize all our custom hooks
  const cartState = useCartState();
  const {
    cart,
    cartHydrationState,
    setCart,
    store,
    cartLoading,
    setCartLoading,
    error,
    setError,
    deleteCartFromLocalStorage,
    updateCartAttributes: updateCartAttributesFromState,
  } = cartState;

  // Sync cart store and currency from RegionLocaleContext (single source of truth)
  const {
    store: regionStore,
    currency: regionCurrency,
    region,
  } = useRegionLocale();
  const [currency, setCurrency] = useState(regionCurrency || null);
  useEffect(() => {
    if (regionStore) {
      cartState.setStore(regionStore);
    }
    if (regionCurrency) {
      setCurrency(regionCurrency);
    }
  }, [regionStore, regionCurrency]);

  // One Zustand store per provider instance (per-client), seeded from the
  // server-resolved region + campaigns so SSR prices include campaign discounts.
  const [cartStoreApi] = useState(() => {
    const seedCampaigns = initialCampaigns ?? [];
    return createCartStore({
      store: regionStore ?? null,
      currency: regionCurrency ?? null,
      campaigns: seedCampaigns,
      campaignActive: seedCampaigns.length > 0,
      getCampaignForProduct: (product) =>
        getCampaignByProduct(seedCampaigns, product),
    });
  });

  const uiState = useCartUI();
  const {
    cartVisible,
    altContent,
    setAltContent,
    isUpsellVisible,
    primaryProductForUpsell,
    showCart,
    hideCart,
    showUpsellModal,
    hideUpsellModal,
  } = uiState;

  useEffect(() => {
    if (isShopPage && isUpsellVisible) {
      hideUpsellModal();
    }
  }, [isShopPage, isUpsellVisible, hideUpsellModal]);

  const cartType = useMemo(() => deriveCartType(cart, store), [cart, store]);
  const ultrahumanXAdded = useMemo(
    () => findUhxLine(cart, store),
    [cart, store],
  );

  const {
    campaigns,
    delivery: sddDelivery,
    refetchQuiet: refetchCampaigns,
    loading: campaignsLoading,
    error: campaignsError,
  } = useCampaigns(initialCampaigns, initialSddDelivery);

  const discountManager = useDiscountManager(
    store,
    cartType,
    ultrahumanXAdded,
    cart,
    discountCodes,
    campaigns,
  );
  const { setDiscountCodes, applicableDiscountCoupons } = discountManager;

  // Additional state that wasn't extracted yet
  const [activeFlowScreen, setActiveFlowScreen] = useState<CartFlowScreens>(
    CartFlowScreens.ITEM_LIST,
  );
  const [cartFlow, setCartFlowState] = useState<CartFlow>(CartFlow.OG);
  const giftFlowMutationInFlightRef = useRef(false);
  const [workAffiliateDiscount, setWorkAfiliateDiscountDetails] =
    useState<workAffiliateDiscountType>();

  const affiliateCode = searchParams?.get('affiliateCode');
  const formatLink = useFormatLink();
  const ls_cart = `cart_${store}`;

  // Work affiliate logic
  const workAffiliate =
    searchParams?.get('workAffiliate') && searchParams?.get('discount');

  useEffect(() => {
    const setData = async () => {
      if (!workAffiliate) return;
      const urlDiscountCode = searchParams?.get('discount') ?? '';
      const stored = readUltraworkSession();
      const sessionMatches =
        Boolean(stored) &&
        Boolean(affiliateCode) &&
        stored?.companyCode === affiliateCode;

      // No token for this company (Google sign-in, another tab, or a 401
      // we already cleared). The coupon on the URL still applies at checkout.
      // The percentage stays 0 because the public company response no longer
      // includes it.
      if (!sessionMatches || !stored) {
        const data = await getWorkAffiliate(affiliateCode as string, store);
        setWorkAfiliateDiscountDetails({
          affiliateCode: data?.affiliate_code ?? (affiliateCode as string) ?? '',
          discount: 0,
          discountCode: urlDiscountCode,
        });
        return;
      }

      const result = await getWorkAffiliateDiscounts(
        stored.companyCode,
        stored.token,
      );
      if (result.status === 'session_invalid') {
        clearUltraworkSession();
      }
      if (result.status !== 'ok') {
        const data = await getWorkAffiliate(affiliateCode as string, store);
        setWorkAfiliateDiscountDetails({
          affiliateCode: data?.affiliate_code ?? (affiliateCode as string) ?? '',
          discount: 0,
          discountCode: urlDiscountCode,
        });
        return;
      }

      setWorkAfiliateDiscountDetails({
        affiliateCode: (affiliateCode as string) ?? '',
        discount: result.discounts.ring_discount ?? 0,
        discountCode: result.discounts.ring_discount_code ?? urlDiscountCode,
      });
    };

    if (workAffiliate) setData();
  }, [workAffiliate, affiliateCode, store]);

  const checkAddonsAddedToCart = useCallback(() => {
    // Addon presence is derived from the cart (see derive.ts); no-op kept for API compat.
  }, []);

  const getCampaignForProduct = useCallback(
    (product: 'ring' | 'ring-pro' | 'home' | 'cgm') => {
      return getCampaignByProduct(campaigns, product);
    },
    [campaigns],
  );

  // Initialize cart operations with all dependencies
  const cartOperations = useCartOperations(
    cartState,
    discountCodes.current,
    setDiscountCodes,
    showCart,
    ultrahumanXAdded,
    checkAddonsAddedToCart,
    setActiveFlowScreen,
    getCampaignForProduct,
    region,
  );

  const {
    addToCartInternal,
    removeFromCart: removeFromCartInternal,
    deleteFromCart: deleteFromCartInternal,
    updateCartQuantity: updateCartQuantityInternal,
    incrementCartQuantity: incrementCartQuantityInternal,
    createCartWithItem,
  } = cartOperations;

  // Wrapper functions to match original CartContextType interface
  const removeFromCart = useCallback(
    async (
      clid: string,
      currentQuantity: number,
      attributes?: Array<Record<string, string>>,
      discountCode?: string,
    ): Promise<void> => {
      await removeFromCartInternal(
        clid,
        currentQuantity,
        attributes,
        discountCode,
      );
    },
    [removeFromCartInternal],
  );

  const deleteFromCart = useCallback(
    async (clid: string): Promise<void> => {
      await deleteFromCartInternal(clid);
    },
    [deleteFromCartInternal],
  );

  const updateCartQuantity = useCallback(
    async (
      clid: string,
      quantity: number,
      attributes?: Array<Record<string, string>>,
    ): Promise<void> => {
      await updateCartQuantityInternal(clid, quantity, attributes);
    },
    [updateCartQuantityInternal],
  );

  const incrementCartQuantity = useCallback(
    async (cartLine: any): Promise<void> => {
      await incrementCartQuantityInternal(cartLine);
    },
    [incrementCartQuantityInternal],
  );

  const addToCartByShopifyVariantId = useCallback(
    async (vid: ProductPurchaseType): Promise<void> => {
      if (!store || !vid || !cart?.id) return;

      try {
        const data = await makeStoreApiRequest(store, `/api/cart/`, {
          cartId: cart.id,
          lineItemsToAdd: [{ merchandiseId: vid, quantity: 1 }],
          discountCodes: discountCodes.current,
        });

        if (data && 'error' in data && data.error) {
          console.error('[Cart] Add to cart failed:', data);
          return;
        }
        if (data && data.cart) {
          cartState.setCart(data.cart);
          showCart();
        } else {
          throw new Error('Failed to add item to cart');
        }
      } catch (error) {
        console.error('Add to cart by variant ID error:', error);
        // Could use toast here if needed
      }
    },
    [store, cart, discountCodes, cartState, showCart],
  );

  // Referral discount calculation
  const referralApplied = searchParams?.get('referral') && store;
  let referralDiscount = 0;
  if (referralApplied) {
    if (cartType === ProductType.M1) {
      referralDiscount = referralCodes[store]?.discount ?? 0;
    } else if (cartType === ProductType.RING_PRO) {
      referralDiscount = ringProReferralCodes[store]?.discount ?? 0;
    } else {
      referralDiscount = ringReferralCodes[store]?.discount ?? 0;
    }
  }

  // Extract variant ID helper
  const extractPrimaryVariantId = useCallback(
    (argsArray: ReadonlyArray<addToCartArgsType>): string | undefined => {
      if (argsArray.length === 0) return undefined;
      const lastItem = argsArray[argsArray.length - 1];
      if (lastItem.shopifyVariantId) {
        return lastItem.shopifyVariantId;
      }
      return undefined;
    },
    [],
  );

  // Main addToCart wrapper with upsell logic
  const addToCart = useCallback(
    async (
      argsArray: ReadonlyArray<addToCartArgsType>,
      showUpsellFlag = true,
    ): Promise<boolean> => {
      const success = await addToCartInternal(
        argsArray,
        false,
        isShopPage ? false : !showUpsellFlag,
      );
      if (!success) return false;

      if (isShopPage) {
        showToast(t('sharedCart.upsell.addedToCart'), 'success');
        return true;
      }

      const isPerfLabProduct = argsArray.some(
        (arg) => arg.productType === ProductType.PERFORMANCE_LAB,
      );
      if (showUpsellFlag && !isUpsellVisible && !isPerfLabProduct) {
        const primaryVariantId = extractPrimaryVariantId(argsArray);
        showUpsellModal(primaryVariantId);
      }
      return true;
    },
    [
      addToCartInternal,
      isShopPage,
      isUpsellVisible,
      extractPrimaryVariantId,
      showUpsellModal,
      showToast,
      t,
    ],
  );

  // Add to cart silently (for upsells)
  const addToCartSilently = useCallback(
    async (items: ReadonlyArray<addToCartArgsType>) => {
      await addToCartInternal(items, false, false, true);
    },
    [addToCartInternal],
  );

  // Proceed with upsell
  const proceedWithUpsell = useCallback(
    async (upsellItems: ReadonlyArray<addToCartArgsType> = []) => {
      if (upsellItems.length > 0) {
        await addToCartInternal(
          upsellItems,
          false,
          isShopPage ? false : true,
          true,
        );
      } else if (!isShopPage) {
        showCart();
      }
      hideUpsellModal();
    },
    [addToCartInternal, isShopPage, showCart, hideUpsellModal],
  );

  // Cart attribute helpers
  const getCartAttributeValue = useCallback(
    (input: string): any => {
      if (!cart || !('attributes' in cart)) return undefined;
      const attributeElement = (
        cart.attributes as Array<{ key: string; value: string }>
      ).filter((e) => e.key === input)[0];
      return attributeElement ? attributeElement.value : undefined;
    },
    [cart],
  );

  const getCartAttributeIndex = useCallback(
    (input: string): any => {
      if (!cart || !('attributes' in cart)) return undefined;
      let op = undefined;
      for (let i = 0; i < cart.attributes.length; i++) {
        if (cart.attributes[i].key === input) {
          op = i;
          break;
        }
      }
      return op;
    },
    [cart],
  );

  // Use updateCartAttributes from cart state hook
  const updateCartAttributes = updateCartAttributesFromState;

  // Set cart flow
  const setCartFlow = useCallback(
    async (input: CartFlow) => {
      if (!cart || !cart.id || giftFlowMutationInFlightRef.current) return;

      giftFlowMutationInFlightRef.current = true;
      setCartFlowState(input);
      if (input === CartFlow.OG) {
        setActiveFlowScreen(CartFlowScreens.ITEM_LIST);
      }

      setCartLoading(true);
      try {
        if (input === CartFlow.OG) {
          const response = await updateCartAttributes(cart.id, [
            { key: 'gift_flow', value: 'false' },
            { key: 'giftee_email', value: 'null' },
            { key: 'gift_note', value: 'null' },
            { key: 'notify_giftee', value: 'false' },
          ]);

          if (!response?.cart?.id) {
            deleteFromLocalStorage(ls_cart);
          } else {
            setCart(
              store
                ? await applyRingAirGiftStamp({
                    cart: response.cart,
                    store,
                    giftOn: false,
                  })
                : response.cart,
            );

            const giftProductCartLine = cart.lines.edges.filter(
              (e: { node: { merchandise: { id: string } } }) =>
                e.node.merchandise.id ===
                GiftWrappingProductShopifyMap[store as ShopifyStore]
                  .variantId,
            );

            if (
              Array.isArray(giftProductCartLine) &&
              giftProductCartLine.length > 0 &&
              giftProductCartLine[0] &&
              giftProductCartLine[0].node.id
            ) {
              await deleteFromCart(giftProductCartLine[0].node.id);
            }
          }
        } else if (input === CartFlow.GIFT) {
          const response = await updateCartAttributes(cart.id, [
            { key: 'gift_flow', value: 'true' },
          ]);

          if (!response?.cart?.id) {
            deleteFromLocalStorage(ls_cart);
          } else {
            setCart(
              store
                ? await applyRingAirGiftStamp({
                    cart: response.cart,
                    store,
                    giftOn: true,
                  })
                : response.cart,
            );

            // Don't add the physical gift-wrap product to an all-digital cart —
            // the gift flow (note, giftee) still applies, just not the wrapping.
            if (!deriveIsAllDigital(cart)) {
              await addToCartSilently([
                {
                  shopifyVariantId:
                    GiftWrappingProductShopifyMap[store as ShopifyStore]
                      .variantId,
                  productType: ProductType.GENERIC,
                },
              ]);
            }
          }
        }
      } finally {
        giftFlowMutationInFlightRef.current = false;
        setCartLoading(false);
      }
    },
    [
      cart,
      store,
      ls_cart,
      updateCartAttributes,
      setCart,
      deleteFromCart,
      addToCartSilently,
      setCartLoading,
    ],
  );

  // Effect to sync cart flow state with cart attributes
  useEffect(() => {
    if (cart && 'id' in cart && store) {
      const giftFlowFromCart = getCartAttributeValue('gift_flow');

      // Discount codes refresh - sync with cart discount codes
      const discountArray: Array<string> = [];
      cart?.discountCodes?.forEach((e: Record<string, string | boolean>) => {
        discountArray.push(e.code as string);
      });
      setDiscountCodes(discountArray, {});

      // Update cart flow state based on cart attributes (skip while toggle is in flight)
      if (!cartLoading && !giftFlowMutationInFlightRef.current) {
        if (giftFlowFromCart === 'true') {
          setCartFlowState(CartFlow.GIFT);
        } else {
          setCartFlowState(CartFlow.OG);
          setActiveFlowScreen(CartFlowScreens.ITEM_LIST);

          // Remove gift wrapping product if not in gift flow
          if (cart && cart.lines && cart.lines.edges) {
            for (let i = 0; i < cart.lines.edges.length; i++) {
              if (
                cart.lines.edges[i].node.merchandise.id ===
                GiftWrappingProductShopifyMap[store as ShopifyStore].variantId
              ) {
                deleteFromCart(cart.lines.edges[i].node.id);
                break;
              }
            }
          }
        }
      }
    }
  }, [
    cart,
    store,
    cartLoading,
    getCartAttributeValue,
    setDiscountCodes,
    setActiveFlowScreen,
    deleteFromCart,
  ]);

  // Don't load affiliate details if it's just a birthday discount without a valid discount parameter
  const shouldLoadAffiliateDetails =
    affiliateCode &&
    !(affiliateCode === 'birthday' && !searchParams?.get('discount'));

  const { affiliateDetails } = useAffiliateMetadata(
    shouldLoadAffiliateDetails ? (affiliateCode as string) : '',
  );

  // Calculate if any campaign is active
  const campaignActive = campaigns.length > 0;

  // Checkout function (simplified version - full implementation would be added later)
  const checkout = useCallback(async () => {
    setCartLoading(true);
    if (!cart || !store) {
      setCartLoading(false);
      trackFunnelEvent(
        CART_GTM_EVENTS.CHECKOUT_FAILED,
        CART_ANALYTICS_EVENTS.CHECKOUT_FAILED,
        { reason: 'no_cart' },
      );
      return;
    }

    // 0-value accessories-only cart must not reach Shopify; CTA is disabled too.
    if (cartState.hasOnlyNonStandaloneItems) {
      setCartLoading(false);
      trackFunnelEvent(
        CART_GTM_EVENTS.CHECKOUT_FAILED,
        CART_ANALYTICS_EVENTS.CHECKOUT_FAILED,
        { reason: 'non_standalone_only' },
      );
      return;
    }
    try {

    // `_gift` is written on gift toggle / add-to-cart. Checkout only reconciles
    // if a prior write missed; never block redirect if that update fails.
    const giftOn =
      getCartAttributeValue('gift_flow') === 'true' ||
      cartFlow === CartFlow.GIFT;
    const checkoutCart = await applyRingAirGiftStamp({ cart, store, giftOn });
    if (checkoutCart !== cart) {
      setCart(checkoutCart);
    }

    const url = checkoutCart.checkoutUrl;
    const cUrl = new URL(url);

    if (affiliateDetails) {
      const affiliateCode = affiliateDetails.code;
      cUrl.searchParams.append('affiliateCode', affiliateCode);
      cUrl.searchParams.append('affiliate', affiliateCode);
    }

    // Additional checkout logic would go here...

    const formatted = formatLink(cUrl.toString(), {
      appendToExternal: true,
      asObject: false,
    }) as string;

    const nextUrl = new URL(formatted);
    nextUrl.searchParams.set('product_type', 'cyborg');
    nextUrl.searchParams.set('store', (store as string) || '');

    // Inject Meta attribution cookies for CAPI event matching
    try {
      const fbclid = getCookie('_uh_fbclid');
      if (fbclid) nextUrl.searchParams.set('fbclid', String(fbclid));
      const fbp = getCookie('_fbp');
      if (fbp) nextUrl.searchParams.set('_fbp', String(fbp));
      const fbc = getCookie('_fbc');
      if (fbc) nextUrl.searchParams.set('_fbc', String(fbc));
      // getCookie() does not decode, but the value is written encoded.
      const rdtCid = getCookie('_uh_rdt_cid');
      if (rdtCid)
        nextUrl.searchParams.set('rdt_cid', decodeURIComponent(String(rdtCid)));
    } catch (_) {
      // cookie read may fail in SSR or with restricted permissions
    }

    Array.from(nextUrl.searchParams.entries()).forEach(([k, v]) => {
      if (k === 'email') nextUrl.searchParams.set('checkout[email]', v);
      nextUrl.searchParams.set(`attributes[${k}]`, v);
    });

    // After the attributes copy so this stays a checkout query param.
    nextUrl.searchParams.set('skip_shop_pay', 'true');

    pushEcommerceEvent(
      'begin_checkout',
      {
        currency: String(
          checkoutCart.cost?.totalAmount?.currencyCode ?? currency ?? '',
        ).toUpperCase(),
        value: Number(checkoutCart.cost?.totalAmount?.amount ?? 0),
        items: cartEcommerceItems(checkoutCart),
      },
      { source: 'cart_drawer', store: String(store) },
    );
    trackFunnelEvent(null, CART_ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
      source: 'cart_drawer',
      store: String(store),
      value: Number(checkoutCart.cost?.totalAmount?.amount ?? 0),
    });

    deleteFromLocalStorage(ls_cart);
    router.push(nextUrl.toString());
    } catch (error) {
      setCartLoading(false);
      trackFunnelEvent(
        CART_GTM_EVENTS.CHECKOUT_FAILED,
        CART_ANALYTICS_EVENTS.CHECKOUT_FAILED,
        { reason: 'exception', message: String(error).slice(0, 160) },
      );
      console.error('[Cart] checkout redirect failed', error);
    }
  }, [
    cart,
    store,
    currency,
    affiliateDetails,
    formatLink,
    router,
    setCartLoading,
    cartState.hasOnlyNonStandaloneItems,
    getCartAttributeValue,
    cartFlow,
    setCart,
  ]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      cart,
      cartHydrationState,
      store,
      currency,
      setStore: cartState.setStore,
      isCartVisible: cartVisible,
      showCart,
      hideCart,

      // Upsell modal
      isUpsellVisible,
      showUpsellModal,
      hideUpsellModal,
      proceedWithUpsell,
      addToCartSilently,

      createCartWithItem,
      addToCart,
      addToCartByShopifyVariantId,
      removeFromCart,
      deleteFromCart,
      checkout,
      updateCartAttributes,
      updateCartQuantity,
      incrementCartQuantity,
      altContent,
      setAltContent,
      ultrahumanXAdded,
      referralDiscount,
      cartFlow,
      setCartFlow,
      cartLoading,
      activeFlowScreen,
      setActiveFlowScreen,
      getCartAttributeValue,
      getCartAttributeIndex,
      deleteCartFromLocalStorage,
      affiliateDetails,
      campaignActive,

      // Campaign data
      campaigns,
      sddDelivery,
      refetchCampaigns,
      campaignsLoading,
      campaignsError,
      getCampaignForProduct,

      error,
      setError,
      applicableDiscountCoupons,
      getExpressCartInput: () => ({
        attributes: cartState.urlParams ?? [],
        discountCodes: [...discountCodes.current],
      }),
      taxes: cartState.taxes,
      duty: cartState.duty,
      taxString: cartState.taxString,
      hasOnlyNonStandaloneItems: cartState.hasOnlyNonStandaloneItems,
      cartType,
      workAffiliateDiscount,
    }),
    [
      cart,
      cartHydrationState,
      store,
      currency,
      cartState,
      cartVisible,
      showCart,
      hideCart,
      isUpsellVisible,
      showUpsellModal,
      hideUpsellModal,
      proceedWithUpsell,
      addToCartSilently,
      createCartWithItem,
      addToCart,
      addToCartByShopifyVariantId,
      removeFromCart,
      deleteFromCart,
      checkout,
      updateCartAttributes,
      updateCartQuantity,
      incrementCartQuantity,
      altContent,
      setAltContent,
      ultrahumanXAdded,
      referralDiscount,
      cartFlow,
      setCartFlow,
      cartLoading,
      activeFlowScreen,
      setActiveFlowScreen,
      getCartAttributeValue,
      getCartAttributeIndex,
      deleteCartFromLocalStorage,
      affiliateDetails,
      campaignActive,
      campaigns,
      sddDelivery,
      refetchCampaigns,
      campaignsLoading,
      campaignsError,
      getCampaignForProduct,
      error,
      setError,
      applicableDiscountCoupons,
      cartType,
      workAffiliateDiscount,
    ],
  );

  useEffect(() => {
    cartStoreApi.getState().__setCartState(contextValue);
  }, [cartStoreApi, contextValue]);

  return (
    <CartStoreContext.Provider value={cartStoreApi}>
      {children}
      {isUpsellVisible && (
        <RouteIntlScope>
          <UpsellModal
            onContinueToCart={(selectedUpsellItems) =>
              proceedWithUpsell(selectedUpsellItems)
            }
            onClose={hideUpsellModal}
            primaryVariantId={primaryProductForUpsell}
          />
        </RouteIntlScope>
      )}
    </CartStoreContext.Provider>
  );
};
